import type {
  ApplicationStage,
  ApprovalStepStatus,
  ArchiveExportStatus,
  AssessmentAttemptStatus,
  AttendanceStatus,
  CallOutcome,
  DropOffKind,
  DuplicateFlagStatus,
  ExamSessionStatus,
  InterviewFlagKind,
  InterviewStatus,
  InviteStatus,
  JobOpeningStatus,
  JoiningDocumentState,
  ContactStatus,
  ManagerResponse,
  MessageStatus,
  OfferStatus,
  PanelistState,
  PhoneScreenPlace,
  PositionStatus,
  PostingStatus,
  PreviousOutcome,
  Recommendation,
  RequisitionPriority,
  RequisitionStatus,
  RetentionStatus,
  ReviewStatus,
  ScorecardStatus,
  SlaState,
  StageDecisionOutcome,
  TaskStatus,
  TemplateStatus,
  UserStatus,
  VerificationStatus,
} from '@nest-recruit/shared';
import type { Tone } from './Tone';

// Every status in the app and its tone: one place, so a status reads the same colour on
// every screen (06-qol §11). Picked on Docs/Mockups/status-palette/ (DECISIONS-LOG
// 2026-10-06 "A colour for every status, no grey anywhere"; ADR 0035). Pipeline stages keep
// the tone of their part of hiring so the board still reads left to right.

export const STAGE_TONES: Readonly<Record<ApplicationStage, Tone>> = {
  new: 'new',
  auto_rejected: 'rejected',
  screening: 'screening',
  assessment: 'assessment',
  interview_1: 'interview',
  interview_2: 'interview',
  interview_3: 'interview',
  hold: 'hold',
  offer: 'offer',
  hired_pending_join: 'done',
  joined: 'done',
  rejected: 'rejected',
  withdrawn: 'ended',
  closed_job: 'ended',
  transferred: 'ended',
};

/** R6 (§L31, design card "Review and shortlist"): the conveyor's lanes. */
export const REVIEW_STATUS_TONES: Readonly<Record<ReviewStatus, Tone>> = {
  to_review: 'screening',
  shortlisted: 'done',
  maybe: 'hold',
};

/** R6: the manager's answer. Waiting is the one that needs someone, so it is the attention tone. */
export const MANAGER_RESPONSE_TONES: Readonly<Record<ManagerResponse, Tone>> = {
  not_sent: 'new',
  awaiting: 'attention',
  yes: 'done',
  no: 'rejected',
  hr_proceeded: 'progress',
};

/** R7 §L32: who still needs a call is new; waiting on them is attention; agreed is done; lost is ended. */
export const CONTACT_STATUS_TONES: Readonly<Record<ContactStatus, Tone>> = {
  not_contacted: 'new',
  emailed: 'progress',
  no_answer: 'attention',
  reached: 'progress',
  confirmed: 'done',
  not_interested: 'ended',
  no_response: 'rejected',
  not_coming: 'ended',
};

/** ADR 0079: a phone screening round. Waiting needs a call, as Not contacted does; Out is a rejection. */
export const PHONE_SCREEN_PLACE_TONES: Readonly<Record<PhoneScreenPlace, Tone>> = {
  waiting: 'new',
  passed: 'done',
  out: 'rejected',
};

export const REQUISITION_STATUS_TONES: Readonly<Record<RequisitionStatus, Tone>> = {
  draft: 'new',
  submitted: 'progress',
  in_approval: 'progress',
  approved: 'live',
  rejected: 'rejected',
  converted: 'live',
  on_hold: 'hold',
  filled: 'done',
  partially_filled: 'done',
  cancelled: 'ended',
  reopened: 'live',
};

export const PRIORITY_TONES: Readonly<Record<RequisitionPriority, Tone>> = {
  low: 'new',
  normal: 'progress',
  urgent: 'urgent',
};

export const SLA_TONES: Readonly<Record<SlaState, Tone>> = {
  on_track: 'live',
  warning: 'attention',
  breached: 'urgent',
  paused: 'hold',
  stopped: 'done',
};

export const JOB_STATUS_TONES: Readonly<Record<JobOpeningStatus, Tone>> = {
  draft: 'new',
  open: 'live',
  on_hold: 'hold',
  filled: 'done',
  closed_partial: 'ended',
  cancelled: 'ended',
  reopened: 'live',
};

export const POSTING_STATUS_TONES: Readonly<Record<PostingStatus, Tone>> = {
  draft: 'new',
  published: 'live',
  unpublished: 'ended',
  expired: 'ended',
};

export const POSITION_STATUS_TONES: Readonly<Record<PositionStatus, Tone>> = {
  planned: 'new',
  vacant: 'attention',
  in_recruitment: 'progress',
  filled: 'done',
  becoming_vacant: 'attention',
  frozen: 'hold',
};

/** An archived record of any kind: a seat, a pool. */
export const ARCHIVED_TONE: Tone = 'ended';

export const INTERVIEW_STATUS_TONES: Readonly<Record<InterviewStatus, Tone>> = {
  unscheduled: 'attention',
  slots_offered: 'progress',
  scheduled: 'progress',
  confirmed: 'live',
  rescheduled: 'attention',
  completed: 'done',
  candidate_no_show: 'urgent',
  panelist_no_show: 'urgent',
  cancelled: 'ended',
};

export const SCORECARD_STATUS_TONES: Readonly<Record<ScorecardStatus, Tone>> = {
  draft: 'attention',
  submitted: 'done',
  waived: 'ended',
};

export const RECOMMENDATION_TONES: Readonly<Record<Recommendation, Tone>> = {
  strong_yes: 'done',
  yes: 'live',
  no: 'rejected',
  strong_no: 'rejected',
};

export const DECISION_OUTCOME_TONES: Readonly<Record<StageDecisionOutcome, Tone>> = {
  advance: 'done',
  reject: 'rejected',
  hold: 'hold',
};

export const INTERVIEW_FLAG_TONES: Readonly<Record<InterviewFlagKind, Tone>> = {
  candidate_late: 'attention',
  cut_short: 'attention',
  technical_failure: 'urgent',
};

export const PANELIST_STATE_TONES: Readonly<Record<PanelistState, Tone>> = {
  available: 'live',
  outside_hours: 'hold',
  blocked_out: 'hold',
  booked: 'progress',
};

export const OFFER_STATUS_TONES: Readonly<Record<OfferStatus, Tone>> = {
  draft: 'new',
  in_approval: 'progress',
  approved: 'live',
  rejected: 'rejected',
  sent: 'progress',
  viewed: 'progress',
  negotiating: 'attention',
  accepted: 'done',
  declined: 'rejected',
  expired: 'ended',
  joined: 'done',
  accepted_no_show: 'urgent',
  withdrawn_by_company: 'ended',
};

export const JOINING_DOCUMENT_TONES: Readonly<Record<JoiningDocumentState, Tone>> = {
  missing: 'attention',
  uploaded: 'progress',
  under_review: 'progress',
  verified: 'done',
  rejected: 'rejected',
  expired: 'urgent',
};

export const VERIFICATION_STATUS_TONES: Readonly<Record<VerificationStatus, Tone>> = {
  pending: 'attention',
  verified: 'done',
  rejected: 'rejected',
  not_required: 'ended',
};

export const APPROVAL_STEP_TONES: Readonly<Record<ApprovalStepStatus, Tone>> = {
  pending: 'attention',
  approved: 'done',
  rejected: 'rejected',
  sent_back: 'rejected',
  skipped: 'ended',
};

export const TEMPLATE_STATUS_TONES: Readonly<Record<TemplateStatus, Tone>> = {
  draft: 'attention',
  approved: 'live',
  rejected: 'rejected',
};

export const ATTEMPT_STATUS_TONES: Readonly<Record<AssessmentAttemptStatus, Tone>> = {
  not_started: 'new',
  in_progress: 'live',
  submitted: 'attention',
  auto_graded: 'done',
  manually_graded: 'done',
  expired: 'ended',
  abandoned: 'urgent',
  cancelled: 'ended',
};

/** A marked result: passed, below the pass mark, or waiting on a marker. */
export const RESULT_TONES = {
  passed: 'done',
  below: 'rejected',
  marking: 'attention',
} as const satisfies Readonly<Record<string, Tone>>;

export const EXAM_SESSION_TONES: Readonly<Record<ExamSessionStatus, Tone>> = {
  draft: 'new',
  scheduled: 'progress',
  roster_locked: 'progress',
  in_progress: 'live',
  results_entry: 'attention',
  completed: 'done',
  cancelled: 'ended',
  rescheduled: 'attention',
};

export const ATTENDANCE_TONES: Readonly<Record<AttendanceStatus, Tone>> = {
  registered: 'new',
  present: 'live',
  completed: 'done',
  absent: 'urgent',
  rescheduled: 'attention',
};

export const USER_STATUS_TONES: Readonly<Record<UserStatus, Tone>> = {
  invited: 'attention',
  active: 'live',
  suspended: 'hold',
  deactivated: 'ended',
};

/** A user locked out after too many attempts, whatever their status. */
export const LOCKED_TONE: Tone = 'urgent';

export const INVITE_STATUS_TONES: Readonly<Record<InviteStatus, Tone>> = {
  pending: 'attention',
  accepted: 'done',
  expired: 'ended',
  revoked: 'ended',
};

export const TASK_STATUS_TONES: Readonly<Record<TaskStatus, Tone>> = {
  open: 'attention',
  in_progress: 'progress',
  done: 'done',
  cancelled: 'ended',
};

export const CALL_OUTCOME_TONES: Readonly<Record<CallOutcome, Tone>> = {
  reached: 'done',
  no_answer: 'attention',
  rescheduled: 'attention',
  declined: 'rejected',
};

export const MESSAGE_STATUS_TONES: Readonly<Record<MessageStatus, Tone>> = {
  queued: 'new',
  scheduled: 'new',
  sent: 'progress',
  delivered: 'done',
  failed: 'urgent',
  bounced: 'urgent',
  cancelled: 'ended',
};

export const DUPLICATE_FLAG_TONES: Readonly<Record<DuplicateFlagStatus, Tone>> = {
  open: 'attention',
  confirmed_duplicate: 'progress',
  not_duplicate: 'done',
  merged: 'done',
  unmerged: 'ended',
};

export const PREVIOUS_OUTCOME_TONES: Readonly<Record<PreviousOutcome, Tone>> = {
  in_progress: 'progress',
  not_selected: 'rejected',
  withdrew: 'ended',
  hired: 'done',
  job_closed: 'ended',
  moved: 'progress',
};

export const DROP_OFF_TONES: Readonly<Record<DropOffKind, Tone>> = {
  rejected: 'rejected',
  withdrawn: 'ended',
  no_show: 'urgent',
};

export const RETENTION_STATUS_TONES: Readonly<Record<RetentionStatus, Tone>> = {
  active: 'live',
  due: 'attention',
  anonymised: 'ended',
};

export const ARCHIVE_EXPORT_TONES: Readonly<Record<ArchiveExportStatus, Tone>> = {
  pending: 'new',
  running: 'progress',
  completed: 'done',
  failed: 'urgent',
};

/** A talent pool that is still offered in pickers. */
export const ACTIVE_POOL_TONE: Tone = 'live';
